// frontend/src/pages/NewUIGamePage.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { X, ArrowLeft, RefreshCw } from 'lucide-react';
import axios from '../config/axios';
import { useAuth } from '../contexts/AuthContext';

const NewUIGamePage = () => {
    const { gameId } = useParams();
    const { user } = useAuth();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [progress, setProgress] = useState(0);
    const [gameData, setGameData] = useState(null);
    const [showConfirmExit, setShowConfirmExit] = useState(false);
    const [unityInstance, setUnityInstance] = useState(null);
    const [canvasReady, setCanvasReady] = useState(false);

    const canvasRef = useRef(null);
    const unityContainerRef = useRef(null);
    const canvasId = `unity-canvas-${gameId}`;

    useEffect(() => {
        const fetchGameData = async () => {
            try {
                setLoading(true);
                const response = await axios.get(`/games/truco/game/${gameId}/server-info`);
                if (response.data.success) {
                    setGameData(response.data.serverInfo);
                } else {
                    setError(response.data.message || 'No se pudo obtener la información del servidor');
                }
            } catch (error) {
                setError(
                    error.response?.data?.message ||
                    'Error al conectar con el servidor. Intenta de nuevo más tarde.'
                );
            } finally {
                setLoading(false);
            }
        };
        fetchGameData();
    }, [gameId]);

    useEffect(() => {
        if (canvasRef.current) {
            canvasRef.current.id = canvasId;
            setTimeout(() => {
                if (document.getElementById(canvasId)) {
                    setCanvasReady(true);
                } else {
                    setError("Error al preparar el entorno del juego");
                }
            }, 100);
        }
    }, [canvasId]);

    useEffect(() => {
        if (!gameData || !canvasReady || !canvasRef.current) return;

        const unityBasePath = "/unity/TrucoWebGL";
        const loaderUrl = `${unityBasePath}/o_WebClientBuild.loader.js`;
        const config = {
            dataUrl: `${unityBasePath}/o_WebClientBuild.data`,
            frameworkUrl: `${unityBasePath}/o_WebClientBuild.framework.js`,
            codeUrl: `${unityBasePath}/o_WebClientBuild.wasm`,
            streamingAssetsUrl: "StreamingAssets",
            companyName: "ManoAMano",
            productName: "Truco Online",
            productVersion: "1.0",
            canvasId: canvasId
        };

        const script = document.createElement("script");
        script.src = loaderUrl;
        script.async = true;
        script.onload = () => {
            if (typeof window.createUnityInstance !== 'function') {
                setError("Error al cargar el motor de Unity. Por favor, intenta de nuevo más tarde.");
                return;
            }

            const canvasElement = document.getElementById(canvasId);
            if (!canvasElement) {
                setError("Error al inicializar el juego. Canvas no encontrado.");
                return;
            }

            window.createUnityInstance(canvasElement, config, (progress) => {
                setProgress(Math.round(progress * 100));
            }).then((instance) => {
                setUnityInstance(instance);
                setLoading(false);
                setTimeout(() => {
                    sendConnectionDataToUnity(instance, {
                        matchId: gameId,
                        serverHost: gameData.host,
                        serverPort: gameData.port,
                        wsPath: gameData.wsPath,
                        wsUrl: gameData.wsUrl,
                        connectionMode: 'WS_GATEWAY',
                        token: gameData.token,
                        userId: user.id,
                        userName: user.username
                    });
                }, 1000);
            }).catch(() => {
                setError('Error al cargar el juego. Por favor, intenta de nuevo más tarde.');
            });
        };

        script.onerror = () => {
            setError("No se pudo cargar el motor del juego. Por favor, verifica tu conexión a internet.");
        };

        document.body.appendChild(script);

        return () => {
            if (unityInstance) {
                try { unityInstance.Quit(); } catch (e) { /* ignored */ }
            }
            if (document.body.contains(script)) {
                document.body.removeChild(script);
            }
        };
    }, [gameData, canvasReady, gameId, user, canvasId]);

    const sendConnectionDataToUnity = (instance, data) => {
        if (!instance) return;
        try {
            instance.SendMessage('Client', 'ReceiveConnectionData', JSON.stringify(data));
        } catch (error) {
            setError("Error al conectar con el servidor de juego. Por favor, intenta de nuevo más tarde.");
        }
    };

    const handleExit = () => setShowConfirmExit(true);

    const confirmExit = () => {
        if (unityInstance) {
            try { unityInstance.Quit(); } catch (e) { /* ignored */ }
        }
        navigate(`/tournaments/${gameId}`);
    };

    const cancelExit = () => setShowConfirmExit(false);

    const handleRetry = () => window.location.reload();

    if (error) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen p-4" style={{ backgroundColor: '#0A1929' }}>
                <div className="p-4 sm:p-6 rounded-lg shadow-lg max-w-md w-full" style={{ backgroundColor: '#0E2238' }}>
                    <h2 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4 text-red-400">Error al cargar el juego</h2>
                    <p className="mb-4 sm:mb-6 text-sm sm:text-base text-gray-300">{error}</p>
                    <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
                        <button
                            onClick={() => navigate(`/tournaments/${gameId}`)}
                            className="flex items-center justify-center gap-2 text-white py-2 px-4 rounded text-sm sm:text-base transition-colors"
                            style={{ backgroundColor: '#122436' }}
                            onMouseEnter={e => e.currentTarget.style.backgroundColor = '#1a3a5c'}
                            onMouseLeave={e => e.currentTarget.style.backgroundColor = '#122436'}
                        >
                            <ArrowLeft size={16} />
                            Volver
                        </button>
                        <button
                            onClick={handleRetry}
                            className="flex items-center justify-center gap-2 text-white py-2 px-4 rounded text-sm sm:text-base transition-colors"
                            style={{ backgroundColor: '#2284DF' }}
                            onMouseEnter={e => e.currentTarget.style.backgroundColor = '#1a6ec0'}
                            onMouseLeave={e => e.currentTarget.style.backgroundColor = '#2284DF'}
                        >
                            <RefreshCw size={16} />
                            Reintentar
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div
            className="flex flex-col items-center justify-center min-h-screen overflow-auto py-8 px-4 sm:px-6 md:px-8 lg:px-12"
            style={{ backgroundColor: '#0A1929' }}
        >
            <h1 className="text-2xl sm:text-3xl font-bold text-white mb-4">Truco Online</h1>

            <div className="w-full max-w-6xl bg-black rounded-lg shadow-2xl overflow-hidden">
                <div className="relative" style={{ paddingTop: "56.25%" }}>
                    <div ref={unityContainerRef} className="absolute top-0 left-0 w-full h-full">
                        <canvas
                            id={canvasId}
                            ref={canvasRef}
                            className="w-full h-full bg-black"
                            style={{ display: loading ? 'none' : 'block' }}
                        />
                    </div>

                    {loading && (
                        <div className="absolute inset-0 flex flex-col items-center justify-center z-10" style={{ backgroundColor: '#0A1929' }}>
                            <h1 className="text-xl sm:text-2xl font-bold text-white mb-2">Cargando Truco Online</h1>
                            <p className="text-gray-400 mb-4 sm:mb-6 text-sm sm:text-base">Preparando la partida...</p>
                            <div className="w-48 sm:w-64 h-3 sm:h-4 rounded-full overflow-hidden" style={{ backgroundColor: '#122436' }}>
                                <div
                                    className="h-full transition-all duration-300 ease-out"
                                    style={{ width: `${progress}%`, backgroundColor: '#2284DF' }}
                                />
                            </div>
                            <p className="text-gray-400 mt-2 text-sm">{progress}%</p>
                        </div>
                    )}
                </div>
            </div>

            <div className="mt-4 text-gray-400 text-sm">
                <p>Servidor: {gameData?.host || 'Conectando...'} • Match ID: {gameId}</p>
            </div>

            <button
                onClick={handleExit}
                className="fixed top-4 right-4 z-20 bg-red-600 hover:bg-red-700 text-white p-2 rounded-full shadow-lg flex items-center justify-center transition-colors"
                aria-label="Salir del juego"
            >
                <X size={24} />
            </button>

            {showConfirmExit && (
                <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-30 p-4">
                    <div className="p-4 sm:p-6 rounded-lg shadow-lg max-w-md w-full" style={{ backgroundColor: '#0E2238' }}>
                        <h3 className="text-lg sm:text-xl font-bold text-white mb-3 sm:mb-4">¿Seguro que deseas salir?</h3>
                        <p className="text-gray-300 mb-4 sm:mb-6 text-sm sm:text-base">
                            Si sales ahora, podrías perder la partida actual.
                            ¿Estás seguro de que quieres abandonar el juego?
                        </p>
                        <div className="flex justify-end space-x-3 sm:space-x-4">
                            <button
                                onClick={cancelExit}
                                className="px-3 sm:px-4 py-1.5 sm:py-2 text-white rounded text-sm sm:text-base transition-colors"
                                style={{ backgroundColor: '#122436' }}
                                onMouseEnter={e => e.currentTarget.style.backgroundColor = '#1a3a5c'}
                                onMouseLeave={e => e.currentTarget.style.backgroundColor = '#122436'}
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={confirmExit}
                                className="px-3 sm:px-4 py-1.5 sm:py-2 bg-red-600 hover:bg-red-700 text-white rounded text-sm sm:text-base transition-colors"
                            >
                                Salir
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default NewUIGamePage;
